'use client';

import { type CSSProperties, type KeyboardEvent, type ReactNode, useEffect, useRef } from 'react';
import {
  type BenchTest,
  formatDate,
  formatMoney,
  OVERDUE_COLOR,
  type OrderKind,
  type OrderStatus,
  RACK_TYPE_SHORT,
  type RackType,
  type StageView,
  stageIndex,
  stagesFor,
  statusMeta,
  type FileRef,
} from '@reikraft/shared';
import { Icon } from './icons';

export function cx(...parts: (string | false | null | undefined)[]) {
  return parts.filter(Boolean).join(' ');
}

// ─── Логотип ────────────────────────────────────────────────────────────

export function LogoMark({ size = 36 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <rect width="32" height="32" fill="#FF5A1F" />
      <path d="M4 20 V14 H7 V20 H10 V14 H13 V20 H16 V14 H19 V20 H22 V14 H25 V20 H28" fill="none" stroke="#0D0E10" strokeWidth="2" />
      <rect x="4" y="21" width="24" height="4" fill="#0D0E10" />
    </svg>
  );
}

/** Содержимое логотипа; оборачивается ссылкой приложения */
export function LogoContent({ brand = 'РЕЙКРАФТ', sub, compact }: { brand?: string; sub?: string; compact?: boolean }) {
  return (
    <>
      <LogoMark size={compact ? 28 : 36} />
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
        <span className="rk-logo__word">{brand}</span>
        {sub && <span className="rk-logo__sub">{sub}</span>}
      </span>
    </>
  );
}

// ─── Статусы ────────────────────────────────────────────────────────────

export function StatusBadge({
  status,
  kind,
  audience = 'staff',
  size,
  label,
}: {
  status: OrderStatus;
  kind: OrderKind;
  audience?: 'staff' | 'client';
  size?: 'lg';
  label?: string;
}) {
  const meta = statusMeta(status, kind, audience);
  const attention = audience === 'client' && status === 'approve';
  if (status === 'done' && kind === 'repair' && audience === 'client') {
    return <span className={cx('rk-badge', 'rk-badge--solid', size === 'lg' && 'rk-badge--lg')}>{label ?? meta.label}</span>;
  }
  return (
    <span className={cx('rk-badge', size === 'lg' && 'rk-badge--lg', attention && 'rk-badge--accent')}>
      <span className="rk-dot" style={{ background: meta.color }} />
      {label ?? meta.label}
    </span>
  );
}

export function OverdueBadge({ days }: { days?: number }) {
  return <span className="rk-badge rk-badge--danger">ПРОСРОЧЕН{days ? ` · ${days} ДН` : ''}</span>;
}

/** Мини-линейка этапов: пройдено — светлое, текущий — цвет статуса, впереди — тёмное */
export function StageBar({
  kind,
  status,
  stages,
  fluid,
  label,
}: {
  kind: OrderKind;
  status: OrderStatus;
  stages?: StageView[];
  fluid?: boolean;
  label?: string;
}) {
  const list = stages ?? stagesFor(kind).map((s) => ({ status: s }));
  const cur = stageIndex(kind, status);
  const done = status === 'done';
  return (
    <span
      className={cx('rk-stagebar', fluid && 'rk-stagebar--fluid')}
      role="img"
      aria-label={label ?? `Этап: ${statusMeta(status, kind).name}`}
    >
      {list.map((s, i) => {
        const color = done || i < cur ? 'var(--text)' : i === cur ? statusMeta(status, kind).color : 'var(--line)';
        return <span key={s.status} style={{ background: color }} />;
      })}
    </span>
  );
}

export function StagesList({ stages, tz }: { stages: StageView[]; tz?: string }) {
  return (
    <ol className="rk-stages" aria-label="Этапы заказа">
      {stages.map((s, i) => (
        <li
          key={s.status}
          aria-current={s.state === 'current' ? 'step' : undefined}
          style={{ borderTopColor: s.state === 'done' ? 'var(--text)' : s.state === 'current' ? s.color : 'var(--line)' }}
        >
          <div className="rk-stages__top">
            <span>{String(i + 1).padStart(2, '0')}</span>
            <span>{s.state === 'done' ? 'ГОТОВО' : s.state === 'current' ? 'СЕЙЧАС' : ''}</span>
          </div>
          <div className="rk-stages__name" style={{ color: s.state === 'todo' ? 'var(--muted-2)' : undefined }}>
            {s.name}
          </div>
          <div className="rk-stages__date">{s.at ? formatDayTimeShort(s.at, tz) : '—'}</div>
        </li>
      ))}
    </ol>
  );
}

function formatDayTimeShort(iso: string, tz?: string) {
  const d = new Date(iso);
  const f = new Intl.DateTimeFormat('ru-RU', {
    timeZone: tz ?? 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  return f.format(d).replace(',', '');
}

export function RackTag({ type }: { type: RackType | null | undefined }) {
  if (!type) return null;
  return <span className="rk-tag">{RACK_TYPE_SHORT[type]}</span>;
}

export function Dot({ color, size }: { color: string; size?: 'sm' }) {
  return <span className={cx('rk-dot', size === 'sm' && 'rk-dot--sm')} style={{ background: color }} />;
}

export { OVERDUE_COLOR };

// ─── Показатели ─────────────────────────────────────────────────────────

export function Kpis({ children, min = 150, style }: { children: ReactNode; min?: number; style?: CSSProperties }) {
  return (
    <div className="rk-kpis" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, ...style }}>
      {children}
    </div>
  );
}

export function Kpi({
  label,
  value,
  dot,
  tone,
  note,
  children,
  span,
}: {
  label: ReactNode;
  value: ReactNode;
  dot?: string;
  tone?: 'danger' | 'accent';
  note?: ReactNode;
  children?: ReactNode;
  span?: number;
}) {
  return (
    <div className={cx('rk-kpi', tone && `rk-kpi--${tone}`)} style={span ? { gridColumn: `span ${span}` } : undefined}>
      <div className="rk-kpi__label">
        {dot && <span className="rk-dot" style={{ width: 7, height: 7, background: dot }} />}
        {label}
      </div>
      <div className="rk-kpi__value">{value}</div>
      {note && <div className="rk-kpi__note">{note}</div>}
      {children}
    </div>
  );
}

export function Meter({ value, max, tone }: { value: number; max: number; tone?: 'warn' | 'danger' }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className={cx('rk-meter', tone && `rk-meter--${tone}`)} role="meter" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value}>
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

// ─── Вкладки и переключатели ────────────────────────────────────────────

export interface TabItem<T extends string> {
  id: T;
  label: ReactNode;
  count?: number;
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: TabItem<T>[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div role="tablist" aria-label={label} className="rk-tabs">
      {items.map((t) => (
        <button key={t.id} type="button" role="tab" aria-selected={t.id === value} className="rk-tab" onClick={() => onChange(t.id)}>
          {t.label}
          {t.count !== undefined && <span className="rk-tab__n">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Chips<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: TabItem<T>[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div role="tablist" aria-label={label} className="rk-chips">
      {items.map((t) => (
        <button key={t.id} type="button" role="tab" aria-selected={t.id === value} className="rk-chip" onClick={() => onChange(t.id)}>
          {t.label}
          {t.count !== undefined && (
            <span className="rk-mono" style={{ marginLeft: 6, fontSize: 11, opacity: 0.7 }}>
              {t.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  showLabel,
  disabled,
  size,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  showLabel?: ReactNode;
  disabled?: boolean;
  size?: 'lg';
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={cx('rk-switch', size === 'lg' && 'rk-switch--lg')}
      onClick={() => onChange(!checked)}
    >
      <span className="rk-switch__track">
        <span className="rk-switch__knob" />
      </span>
      {showLabel && <span className="rk-switch__label">{showLabel}</span>}
    </button>
  );
}

export function Choices<T extends string>({
  options,
  value,
  onChange,
  label,
  min = 180,
}: {
  options: { value: T; title: ReactNode; note?: ReactNode; disabled?: boolean }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
  min?: number;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="rk-choices"
      style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))` }}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          disabled={o.disabled}
          className="rk-choice"
          onClick={() => onChange(o.value)}
        >
          <b>{o.title}</b>
          {o.note && <span className="rk-choice__note">{o.note}</span>}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: ReactNode }[];
  value: T | null | undefined;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="rk-segmented">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── Поля формы ─────────────────────────────────────────────────────────

export function Field({
  label,
  error,
  hint,
  children,
  mono,
  className,
  style,
}: {
  label: ReactNode;
  error?: string | null;
  hint?: ReactNode;
  children: ReactNode;
  mono?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <label className={cx('rk-field', mono && 'rk-field--mono-label', className)} style={style}>
      {label}
      {children}
      {hint && !error && <span className="rk-field__hint">{hint}</span>}
      {error && (
        <span className="rk-field__error" role="alert">
          {error}
        </span>
      )}
    </label>
  );
}

export function SearchBox({
  value,
  onChange,
  placeholder,
  label,
  size,
  style,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  label: string;
  size?: 'lg';
  style?: CSSProperties;
}) {
  return (
    <label className={cx('rk-search', size === 'lg' && 'rk-search--lg')} style={style}>
      <Icon name="search" size={size === 'lg' ? 20 : 16} style={{ color: 'var(--muted)', flex: 'none' }} />
      <span className="rk-sr">{label}</span>
      <input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </label>
  );
}

// ─── Панели ─────────────────────────────────────────────────────────────

export function Panel({
  title,
  num,
  actions,
  children,
  footer,
  raised,
  bodyless,
  className,
  style,
  id,
}: {
  title?: ReactNode;
  num?: string;
  actions?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  raised?: boolean;
  bodyless?: boolean;
  className?: string;
  style?: CSSProperties;
  id?: string;
}) {
  return (
    <section className={cx('rk-panel', raised && 'rk-panel--raised', className)} style={style} id={id}>
      {(title || actions) && (
        <div className="rk-panel__head">
          <h2 className="rk-section-title">
            {num && <span className="rk-panel__num">{num}</span>}
            {title}
          </h2>
          {actions && (
            <div className="rk-row" style={{ ['--rk-gap' as string]: '8px' }}>
              {actions}
            </div>
          )}
        </div>
      )}
      {bodyless ? children : children !== undefined && <div className="rk-panel__body">{children}</div>}
      {footer && <div className="rk-panel__foot">{footer}</div>}
    </section>
  );
}

export function Notice({
  tone,
  label,
  children,
  actions,
  dot,
}: {
  tone?: 'accent' | 'success' | 'danger' | 'warn' | 'stand';
  label?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  dot?: string;
}) {
  return (
    <div className={cx('rk-notice', tone && `rk-notice--${tone}`)} role={tone === 'danger' ? 'alert' : 'status'}>
      {label && (
        <span className="rk-notice__label">
          {dot && <span className="rk-dot" style={{ background: dot }} />}
          {label}
        </span>
      )}
      {children && <div style={{ flex: '1 1 320px', minWidth: 0 }}>{children}</div>}
      {actions && (
        <div className="rk-row" style={{ ['--rk-gap' as string]: '8px' }}>
          {actions}
        </div>
      )}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="rk-empty">{children}</div>;
}

export function Loader({ label = 'Загрузка' }: { label?: string }) {
  return (
    <div className="rk-loader" role="status">
      {label}
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : 'Не удалось загрузить данные';
  return (
    <Notice
      tone="danger"
      label="Ошибка"
      actions={
        onRetry && (
          <button type="button" className="rk-btn rk-btn--sm" onClick={onRetry}>
            Повторить
          </button>
        )
      }
    >
      {msg}
    </Notice>
  );
}

// ─── Таблица ────────────────────────────────────────────────────────────

export interface Column<T> {
  key: string;
  title: ReactNode;
  width: string;
  align?: 'right';
  render: (row: T) => ReactNode;
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowClassName,
  minWidth = 760,
  empty,
  label,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  minWidth?: number;
  empty?: ReactNode;
  label?: string;
}) {
  const cols = columns.map((c) => c.width).join(' ');
  const keyDown = (row: T) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' && onRowClick) onRowClick(row);
  };
  return (
    <div className="rk-table-wrap">
      <div
        className="rk-table"
        role="table"
        aria-label={label}
        style={{ ['--rk-table-min' as string]: `${minWidth}px`, ['--rk-cols' as string]: cols }}
      >
        <div className="rk-tr rk-tr--head" role="row">
          {columns.map((c) => (
            <span key={c.key} role="columnheader" className={cx('rk-td', c.align === 'right' && 'rk-right')}>
              {c.title}
            </span>
          ))}
        </div>
        {rows.map((r) => (
          <div
            key={rowKey(r)}
            role="row"
            className={cx('rk-tr', onRowClick && 'rk-tr--link', rowClassName?.(r))}
            tabIndex={onRowClick ? 0 : undefined}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
            onKeyDown={onRowClick ? keyDown(r) : undefined}
          >
            {columns.map((c) => (
              <span key={c.key} role="cell" className={cx('rk-td', c.align === 'right' && 'rk-right')}>
                {c.render(r)}
              </span>
            ))}
          </div>
        ))}
        {!rows.length && <Empty>{empty ?? 'Ничего не найдено'}</Empty>}
      </div>
    </div>
  );
}

export function Pager({
  page,
  pages,
  total,
  shown,
  onPage,
}: {
  page: number;
  pages: number;
  total: number;
  shown: number;
  onPage: (p: number) => void;
}) {
  if (total === 0) return null;
  const nums: number[] = [];
  const from = Math.max(1, Math.min(page - 2, pages - 4));
  for (let i = from; i <= Math.min(pages, from + 4); i++) nums.push(i);
  return (
    <div className="rk-pager">
      <span>
        ПОКАЗАНО {shown} ИЗ {total}
      </span>
      {pages > 1 && (
        <nav className="rk-pager__pages" aria-label="Страницы">
          {nums.map((n) => (
            <button key={n} type="button" aria-current={n === page ? 'page' : undefined} onClick={() => onPage(n)}>
              {n}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}

// ─── Модальное окно ─────────────────────────────────────────────────────

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      // showModal ставит фокус на первую кнопку («Закрыть») — переносим его на первое поле формы
      const field = d.querySelector<HTMLElement>(
        '.rk-modal__body [autofocus], .rk-modal__body input:not([type=hidden]):not([readonly]), .rk-modal__body select, .rk-modal__body textarea',
      );
      field?.focus();
    }
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className={cx('rk-modal', wide && 'rk-modal--wide')} onClose={onClose} onCancel={onClose}>
      {open && (
        <>
          <div className="rk-modal__head">
            <h2 className="rk-section-title">{title}</h2>
            <button
              type="button"
              className="rk-btn rk-btn--icon"
              aria-label="Закрыть"
              onClick={onClose}
              style={{ borderColor: 'transparent', marginRight: -10 }}
            >
              <Icon name="close" />
            </button>
          </div>
          <div className="rk-modal__body">{children}</div>
          {footer && <div className="rk-modal__foot">{footer}</div>}
        </>
      )}
    </dialog>
  );
}

// ─── Фото и заглушки ────────────────────────────────────────────────────

/** Чертёж рейки — когда фото ещё нет */
export function RackDrawing({ stroke = '#A0A4AB' }: { stroke?: string }) {
  return (
    <svg viewBox="0 0 240 80" width="100%" aria-hidden="true" style={{ display: 'block' }}>
      <line x1="0" y1="44" x2="240" y2="44" stroke="#3A3F46" strokeDasharray="8 3 2 3" />
      <g fill="#15171A" stroke={stroke} strokeWidth="1.2">
        <rect x="62" y="36" width="116" height="16" />
        <rect x="42" y="38" width="20" height="12" />
        <rect x="178" y="38" width="20" height="12" />
        <rect x="16" y="42" width="26" height="4" />
        <rect x="198" y="42" width="26" height="4" />
        <circle cx="12" cy="44" r="5" />
        <circle cx="228" cy="44" r="5" />
        <rect x="70" y="10" width="14" height="26" transform="rotate(-16 77 36)" />
      </g>
    </svg>
  );
}

export function CardMedia({ cover, rackType, note }: { cover: FileRef | null | undefined; rackType: RackType; note?: ReactNode }) {
  if (cover) {
    return (
      <div className="rk-card__media rk-card__media--photo rk-gridbg">
        <span className="rk-tag">{RACK_TYPE_SHORT[rackType]}</span>
        <img src={cover.url} alt="" loading="lazy" />
      </div>
    );
  }
  return (
    <div className="rk-card__media rk-gridbg">
      <span className="rk-tag">{RACK_TYPE_SHORT[rackType]}</span>
      <span className="rk-card__media-note">{note ?? 'ФОТО'}</span>
      <RackDrawing />
    </div>
  );
}

export function PhotoGrid({
  photos,
  onRemove,
  onAdd,
  addLabel = 'Загрузить',
  uploading,
  coverFirst,
  labels,
  min = 120,
  // .heic явно: на Windows фильтр image/* прячет фото с iPhone; API приводит их к JPEG
  accept = 'image/*,.heic,.heif',
  capture,
}: {
  photos: FileRef[];
  onRemove?: (id: string) => void;
  onAdd?: (files: FileList) => void;
  addLabel?: string;
  uploading?: boolean;
  coverFirst?: boolean;
  labels?: (p: FileRef, i: number) => string | null;
  min?: number;
  accept?: string;
  capture?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="rk-photos" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))` }}>
      {photos.map((p, i) => {
        const label = coverFirst && i === 0 ? 'ОБЛОЖКА' : (labels?.(p, i) ?? null);
        return (
          <div key={p.id} className={cx('rk-photo', coverFirst && i === 0 && 'rk-photo--cover')}>
            <a href={p.url} target="_blank" rel="noreferrer" aria-label={`Открыть фото ${i + 1}`}>
              {p.mime === 'application/pdf' ? (
                <span className="rk-mono" style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                  PDF
                </span>
              ) : (
                <img src={p.url} alt={p.name ?? ''} loading="lazy" />
              )}
            </a>
            {label && <span className="rk-photo__label">{label}</span>}
            {onRemove && (
              <button type="button" className="rk-photo__remove" aria-label="Убрать фото" onClick={() => onRemove(p.id)}>
                ×
              </button>
            )}
          </div>
        );
      })}
      {onAdd && (
        <>
          <button type="button" className="rk-photo-add" onClick={() => input.current?.click()} disabled={uploading}>
            <Icon name={capture ? 'camera' : 'plus'} size={20} />
            {uploading ? 'Загрузка…' : addLabel}
          </button>
          <input
            ref={input}
            type="file"
            accept={accept}
            multiple
            hidden
            {...(capture ? { capture: 'environment' as const } : {})}
            onChange={(e) => {
              if (e.target.files?.length) onAdd(e.target.files);
              e.target.value = '';
            }}
          />
        </>
      )}
    </div>
  );
}

// ─── Протокол стенда ────────────────────────────────────────────────────

export function BenchPaper({
  bench,
  brand = 'РЕЙКРАФТ',
  info,
  tz,
  compact,
  footer,
}: {
  bench: BenchTest;
  brand?: string;
  info?: { label: string; value: ReactNode; mono?: boolean }[];
  tz?: string;
  compact?: boolean;
  footer?: ReactNode;
}) {
  return (
    <div className="rk-paper">
      <div className="rk-paper__head">
        <div>
          <div className="rk-mono" style={{ fontSize: 11, letterSpacing: '0.1em' }}>
            {brand} · ОТК
          </div>
          <div className="rk-paper__title" style={compact ? { fontSize: 13 } : undefined}>
            Протокол стенда
          </div>
        </div>
        <div className="rk-mono" style={{ fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap' }}>
          № {bench.number}
          <br />
          {formatDate(bench.testedAt, tz)}
        </div>
      </div>
      {info && info.length > 0 && (
        <div className="rk-paper__info">
          {info.map((i) => (
            <div key={i.label}>
              <span className="rk-paper__k">{i.label}:</span> <span className={i.mono ? 'rk-mono' : undefined}>{i.value}</span>
            </div>
          ))}
        </div>
      )}
      <div>
        {!compact && (
          <div className="rk-paper__row rk-paper__row--head">
            <span>ПАРАМЕТР</span>
            <span>ДОПУСК</span>
            <span>ИТОГ</span>
          </div>
        )}
        {bench.params.map((p) => (
          <div key={p.name} className="rk-paper__row" style={compact ? { gridTemplateColumns: 'minmax(0,1fr) auto' } : undefined}>
            <span>{p.name}</span>
            {!compact && <span>{p.tolerance || p.value || '—'}</span>}
            <b>{p.result === 'ok' ? 'ОК' : 'НЕ ОК'}</b>
          </div>
        ))}
      </div>
      {footer ?? (
        <div className="rk-paper__foot">
          <div style={{ lineHeight: 2 }}>
            <div>Мастер: {bench.masterName ?? '____________'}</div>
            <div>Контролёр: ____________</div>
          </div>
          <div className="rk-stamp" style={bench.passed ? undefined : { borderColor: '#B3261E', color: '#B3261E' }}>
            ОТК
            <br />
            {bench.passed ? 'ПРИНЯТО' : 'БРАК'}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Деньги ─────────────────────────────────────────────────────────────

export function Money({ kop, empty }: { kop: number | null | undefined; empty?: string }) {
  return <>{formatMoney(kop, { empty })}</>;
}
